/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import React from 'react';
import {makeStyles} from '@material-ui/core/styles';
import {toggleThemeStyle} from '@ion/dls/src/theme/helpers';
import {dlsColor, dlsTypography, dlsZIndex} from '@ion/dls/src/theme/theme';

interface WithStylesProps {
  isMobileMapView?: boolean;
};

// the height the grid leaves the current theme's header block (the header,
// banner and summary cards) - a taller block takes what it needs from the
// grid. Then the "no trips / no assets" banner's height and its bottom
// margin (the legacy theme's only), and the current theme's summary row
// height ...
export const MAIN_WRAPPER_HEIGHT = 120;
const NO_DATA_HEIGHT             = 52;
const NO_DATA_MARGIN             = 18;
const DATA_ROW_HEIGHT            = 88;

const useStyles = makeStyles(theme => ({
  loadingContainer : {
    width                          : '100%',
    height                         : 'calc(100vh - (490px))',
    display                        : 'flex',
    alignItems                     : 'center',
    justifyContent                 : 'center',
    [theme.breakpoints.down('sm')] : {
      height : '70vh'
    }
  },
  map : {
    display : 'flex',
    height  : '100%',
    width   : '100%'
  },
  toggleView : {
    width  : '100px',
    height : '100px'
  },
  assetHistory : {
    height                         : '100%',
    display                        : 'flex',
    width                          : '100%',
    placeContent                   : 'flex-end',
    flexDirection                  : 'column',
    [theme.breakpoints.down('sm')] : {
      padding         : ({isMobileMapView}: WithStylesProps) => isMobileMapView ? '0px' : '0 16px',
      backgroundColor : dlsColor('white'),
      perspective     : '1000px'
    }
  },
  assetHistoryInner : {
    display                        : 'flex',
    height                         : '100%',
    overflowY                      : 'hidden',
    [theme.breakpoints.down('sm')] : {
      display        : 'block',
      overflowY      : 'initial',
      position       : 'relative',
      width          : '100%',
      height         : '100%',
      textAlign      : 'center',
      transition     : 'transform 1s',
      transformStyle : 'preserve-3d',
      transform      : ({isMobileMapView}: WithStylesProps) => isMobileMapView ? 'rotateY(180deg)' : 'rotateY(0deg)'
    }
  },
  assetHistoryContent : {
    position                       : 'relative',
    width                          : 'calc(100% - 40px)',
    height                         : '100%',
    display                        : ({isMobileMapView}: WithStylesProps) => isMobileMapView ? 'none' : 'block',
    padding                        : '0px 20px',
    [theme.breakpoints.down('sm')] : {
      position           : 'absolute',
      height             : '100%',
      backfaceVisibility : 'hidden',
      padding            : '0px',
      transformStyle     : 'preserve-3d',
      overflow           : 'hidden'
    }
  },
  mapDisplay : {
    width                          : '100%',
    height                         : '100%',
    [theme.breakpoints.down('sm')] : {
      position           : 'absolute',
      height             : '100%',
      backfaceVisibility : 'hidden',
      display            : ({isMobileMapView}: WithStylesProps) => isMobileMapView ? 'contents' : 'none',
      zIndex             : dlsZIndex('assetHistoryMapDisplay')
    }
  },
  eventMenuSelector : {
    margin : '0px 24px 15px 0px'
  },
  assetHistoryHeader : {
    display                        : 'flex',
    justifyContent                 : 'space-between',
    alignItems                     : 'center',
    flexWrap                       : 'nowrap',
    overflow                       : 'hidden',
    [theme.breakpoints.down('sm')] : {
      display : 'block'
    }
  },
  assetHistoryTitle : {
    fontSize                       : '20px',
    lineHeight                     : '30px',
    fontWeight                     : 500,
    color                          : dlsColor('black'),
    display                        : 'flex',
    justifyContent                 : 'space-between',
    alignItems                     : 'center',
    overflow                       : 'hidden',
    whiteSpace                     : 'nowrap',
    marginRight                    : '25px',
    minWidth                       : 'fit-content',
    [theme.breakpoints.down('sm')] : {
      flexWrap    : 'wrap',
      marginRight : '0px'
    }
  },
  historyTitle : {
    display                        : 'flex',
    justifyContent                 : 'flex-end',
    alignItems                     : 'center',
    zIndex                         : dlsZIndex('assetHistoryTitle'),
    [theme.breakpoints.down('sm')] : {
      justifyContent : 'flex-start'
    }
  },
  historyTitleText : {
    ...dlsTypography('caption'),
    display                        : 'flex',
    flexWrap                       : 'nowrap',
    color                          : dlsColor('grey1'),
    textTransform                  : 'uppercase',
    fontWeight                     : 700,
    letterSpacing                  : '1px',
    whiteSpace                     : 'nowrap',
    [theme.breakpoints.down('sm')] : {
      flexWrap     : 'wrap',
      whiteSpace   : 'nowrap',
      marginBottom : '4px'
    }
  },
  groupText : {
    marginRight                    : '12px',
    paddingRight                   : '12px',
    borderRight                    : `solid 1px ${dlsColor('grey1')}`,
    [theme.breakpoints.down('sm')] : {
      borderRight : 'none'
    }
  },
  date : {
    fontSize      : '16px',
    lineHeight    : '24px',
    fontWeight    : 'bold',
    fontFamily    : 'Roboto',
    letterSpacing : '1px',
    color         : dlsColor('grey3'),
    marginTop     : '5px'
  },
  noResults : {
    margin : '25px 0'
  },
  // the current theme's cards and grid controls row - 88px, both centred
  // in it ...
  dataWrapper : {
    display                        : 'flex',
    overflow                       : 'hidden',
    minWidth                       : toggleThemeStyle('728px', 'auto'),
    justifyContent                 : toggleThemeStyle('inherit', 'space-between'),
    height                         : toggleThemeStyle('auto', `${DATA_ROW_HEIGHT}px`),
    alignItems                     : toggleThemeStyle('normal', 'center'),
    [theme.breakpoints.down('sm')] : {
      flexDirection : 'column'
    }
  },
  // as tall as the header, banner and cards need, none of them squeezed ...
  mainWrapper : {
    height        : 'auto',
    display       : 'flex',
    flexDirection : 'column',
    overflow      : 'hidden',
    '& > *'       : {
      flexShrink : toggleThemeStyle(1, 0)
    },
    [theme.breakpoints.down('sm')] : {
      marginTop : '10px'
    }
  },
  dataGraphics : {
    gap                            : toggleThemeStyle('inherit', '8px'),
    display                        : 'flex',
    justifyContent                 : 'flex-start',
    width                          : '100%',
    [theme.breakpoints.down('sm')] : {
      justifyContent : 'space-between',
      marginBottom   : '22px'
    },
    [theme.breakpoints.down('xs')] : {
      justifyContent : 'center'
    }
  },
  dataRow : {
    display                        : 'flex',
    flexWrap                       : 'wrap',
    marginBottom                   : '23px',
    [theme.breakpoints.down('sm')] : {
      marginBottom   : '10px',
      marginRight    : '8px',
      justifyContent : 'space-evenly'
    },
    [theme.breakpoints.down('xs')] : {
      justifyContent : 'center',
      marginBottom   : '0px'
    }
  },
  dataCard : {
    marginLeft       : 0,
    '&:last-of-type' : {
      marginRight : '0px'
    }
  },
  noAsset : {
    ...dlsTypography('body2'),
    color                          : dlsColor('grey2'),
    backgroundColor                : dlsColor('paleBlue'),
    width                          : '100%',
    height                         : `${NO_DATA_HEIGHT}px`,
    display                        : 'flex',
    alignItems                     : 'center',
    marginBottom                   : toggleThemeStyle(`${NO_DATA_MARGIN}px`, 0),
    overflow                       : 'hidden',
    borderTop                      : `4px solid ${dlsColor('caBlue')}`,
    [theme.breakpoints.down('sm')] : {
      whiteSpace   : 'normal',
      padding      : '0px 12px',
      width        : 'auto',
      marginBottom : '0px',
      marginTop    : '18px'
    }
  },
  noAssetIcon : {
    marginLeft                     : '26px',
    display                        : 'flex',
    alignItems                     : 'center',
    [theme.breakpoints.down('sm')] : {
      marginLeft : '0px'
    }
  },
  noAssetText : {
    marginLeft                     : '8px',
    fontSize                       : '14px',
    lineHeight                     : '20px',
    fontWeight                     : 400,
    fontFamily                     : 'Roboto',
    whiteSpace                     : 'nowrap',
    overflow                       : 'hidden',
    [theme.breakpoints.down('sm')] : {
      whiteSpace : 'normal'
    }
  },
  tableLoadingContainer : {
    display        : 'flex',
    justifyContent : 'center',
    alignItems     : 'center',
    height         : '150px'
  },
  cardTombstone : {
    marginRight : '4px'
  },
  tableTombstone : {
    width     : '100%',
    height    : '400px',
    marginTop : '47px'
  },
  backdrop : {
    zIndex : dlsZIndex('assetHistoryBackdrop')
  },
  backdropDesktop : {
    zIndex   : dlsZIndex('contentLoaderContainer'),
    position : 'absolute'
  },
  breadCrumbWrapper : {
    display        : 'flex',
    justifyContent : 'flex-start',
    alignItems     : 'center',
    background     : dlsColor('grey10'),
    paddingLeft    : '12px',
    borderRadius   : '4px 4px 0px 0px',
    marginTop      : 0,
    '& > span'     : {
      fontWeight  : 500,
      color       : dlsColor('white'),
      marginRight : '5px',
      ...dlsTypography('chipButton')
    },
    '& > span:last-child' : {
      color : dlsColor('white')
    },
    '& > span:first-child' : {
      lineHeight  : 0,
      marginRight : 0
    }
  },
  breadcrumbSeparator : {
    color : dlsColor('white')
  },
  header : {
    color    : dlsColor('white'),
    fontSize : '12px !important'
  },
  title : {
    fontFamily : 'Roboto',
    color      : dlsColor('white'),
    fontWeight : 700,
    fontSize   : '16px',
    lineHeight : '20px'
  },
  content : {
    fontFamily : 'Roboto',
    color      : dlsColor('white'),
    fontWeight : 400,
    fontSize   : '16px',
    lineHeight : '20px',
    display    : 'flex'
  },
  controlsBank : {
    marginLeft : 'auto'
  },
  controlCtas : {
    flex           : 1,
    display        : 'inline-flex',
    alignItems     : 'center',
    marginRight    : '-12px',
    marginBottom   : toggleThemeStyle('-12px', 0),
    justifyContent : 'flex-end'
  }
}) as any);

const useAssetHistoryStyle = () => {
  return {
    useStyles
  };
};

export default useAssetHistoryStyle;
